import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { invoices as api, fieldWorks as fieldWorksApi, type FieldWork } from '@/api/endpoints';
import type { Invoice, InvoiceDetail, InvoiceHistoryEntry, InvoiceMeta, InvoicePaymentStatus } from '@/types';
import { hasPermission } from '@/types';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { ArchiveFilesCard } from '@/components/ArchiveFilesCard';
import { useAuth } from '@/auth/AuthContext';
import { formatCurrency } from '@/lib/money';

const fmtDateTime = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('pl-PL', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

/** „1 dzień" / „11 dni" — odmiana rzeczownika przy liczbie dni. */
const dni = (n: number): string => `${n} ${n === 1 ? 'dzień' : 'dni'}`;

/** Liczba dni od dziś do daty ISO (ujemna = data w przeszłości). Null gdy brak/niepoprawna data. */
const daysFromToday = (iso?: string | null): number | null => {
  if (!iso) return null;
  const target = new Date(iso + 'T00:00:00');
  if (isNaN(target.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
};

const EVENT_LABELS: Record<string, string> = {
  INVOICE_IMPORT: 'Import z KSeF',
  PAYMENT_STATUS_CHANGE: 'Zmiana statusu płatności',
  BANK_STATEMENT_IMPORT: 'Import wyciągu bankowego',
  EXTERNAL_API_CALL: 'Wywołanie API zewnętrznego',
  CREATE: 'Utworzenie',
  EDIT: 'Edycja',
  DELETE: 'Usunięcie',
};

/**
 * Podgląd faktury (Finanse, /faktury/:id) — wizualizacja danych pobranych z KSeF: nagłówek jak na
 * dokumencie (strony, daty, kwoty netto/VAT/brutto), pasek rozliczenia i terminu płatności,
 * powiązane przelewy z wyciągu bankowego oraz historia zdarzeń dokumentu. Tu prowadzą też
 * powiadomienia „Nowa faktura" (link /faktury/{id}).
 */
export function FakturaDetailPage() {
  const { id } = useParams();
  const invId = Number(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = hasPermission(user, 'FINANCE_MANAGE');
  const canSeeWorks = hasPermission(user, 'FIELDWORK_VIEW');

  const [data, setData] = useState<InvoiceDetail | null>(null);
  const [works, setWorks] = useState<FieldWork[] | null>(null);
  const [meta, setMeta] = useState<InvoiceMeta | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [payModal, setPayModal] = useState(false);

  const reload = () => api.detail(invId).then(setData).catch(e => setErr(e?.message ?? String(e)));
  useEffect(() => {
    if (!Number.isFinite(invId)) { setErr('Niepoprawny numer faktury w adresie.'); return; }
    setData(null); setErr(null); reload();
    if (canSeeWorks) {
      setWorks(null);
      fieldWorksApi.byInvoice(invId).then(setWorks).catch(() => setWorks([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invId]);
  useEffect(() => { api.meta().then(setMeta).catch(() => {}); }, []);

  const togglePaid = () => {
    setBusy(true); setErr(null);
    api.togglePaid(invId)
      .then(() => { setBusy(false); reload(); })
      .catch(e => { setBusy(false); setErr(e?.message ?? String(e)); });
  };

  const backBtn = (
    <button onClick={() => navigate('/faktury')} className="btn btn-ghost btn-sm mb-3 text-ink-500">
      <Icon name="arrow" className="w-3.5 h-3.5 rotate-180"/> Faktury
    </button>
  );

  if (err && !data) return <div className="max-w-[1100px]">{backBtn}<div className="text-red-600 text-[13px]">Błąd: {err}</div></div>;
  if (!data) return <div className="max-w-[1100px]">{backBtn}<div className="text-ink-400">Ładowanie…</div></div>;

  const inv = data.invoice;
  const cur = inv.currency || 'PLN';
  const settled = inv.paymentStatus === 'OPLACONA' || inv.paymentStatus === 'ANULOWANA';
  const dueIn = daysFromToday(inv.dueDate);
  const overdue = dueIn != null && dueIn < 0 && inv.remainingAmount > 0 && !settled;
  const paidPct = inv.totalGross > 0
    ? Math.min(100, Math.max(0, Math.round((inv.paidAmount / inv.totalGross) * 100)))
    : (settled ? 100 : 0);

  return (
    <div className="max-w-[1100px]">
      {backBtn}

      <header className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="mb-1">Faktura {inv.invoiceNumber ?? `#${inv.id}`}</h1>
          <div className="flex items-center gap-2 flex-wrap text-[13px] text-ink-500">
            <span className={'pill pill-' + (inv.direction === 'PURCHASE' ? 'earth' : 'water')}>{inv.directionName}</span>
            <span className={'pill pill-' + inv.paymentStatusColor}>{inv.paymentStatusName}</span>
            <span className="pill pill-gray">{inv.source === 'KSEF' ? 'z KSeF' : 'dodana ręcznie'}</span>
            {overdue && <span className="pill pill-red">po terminie o {dni(-dueIn!)}</span>}
          </div>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            {inv.paymentStatus !== 'ANULOWANA' && inv.paymentStatus !== 'SPORNA' && (
              <button onClick={togglePaid} disabled={busy}
                      title={inv.paymentStatus === 'OPLACONA' ? 'Cofnij oznaczenie — do zapłaty' : 'Oznacz jako opłaconą (dziś, pełna kwota)'}
                      className={'btn btn-sm ' + (inv.paymentStatus === 'OPLACONA' ? 'btn-secondary text-green-600' : 'btn-primary')}>
                <Icon name="check" className="w-3.5 h-3.5"/>
                {inv.paymentStatus === 'OPLACONA' ? 'Opłacona' : 'Oznacz opłaconą'}
              </button>
            )}
            <button onClick={() => setPayModal(true)} className="btn btn-secondary btn-sm">
              <Icon name="edit" className="w-3.5 h-3.5"/> Status płatności
            </button>
          </div>
        )}
      </header>

      {err && <div className="text-red-600 mb-3 text-[12px]">Błąd: {err}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_330px] gap-4 items-start">
        {/* ── Dokument: wizualizacja danych pobranych z KSeF ── */}
        <div className="card">
          <div className="card-body !p-6">
            <div className="flex items-start justify-between gap-6 pb-4 border-b border-ink-100 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-ink-400 mb-0.5">
                  {inv.direction === 'PURCHASE' ? 'Faktura otrzymana' : 'Faktura wystawiona'}
                </div>
                <div className="text-[20px] font-semibold text-ink-900 font-mono leading-tight">
                  {inv.invoiceNumber ?? '— brak numeru —'}
                </div>
                {inv.ksefNumber && (
                  <div className="text-[11px] text-ink-400 font-mono mt-1">KSeF: {inv.ksefNumber}</div>
                )}
              </div>
              <div className="text-[12px] grid grid-cols-[auto_auto] gap-x-3 gap-y-1 ml-auto">
                <span className="text-ink-400">Data wystawienia</span>
                <span className="font-mono text-ink-800 text-right">{inv.issueDate ?? '—'}</span>
                <span className="text-ink-400">Data sprzedaży</span>
                <span className="font-mono text-ink-800 text-right">{inv.saleDate ?? '—'}</span>
                <span className="text-ink-400">Termin płatności</span>
                <span className={'font-mono text-right ' + (overdue ? 'text-red-600 font-semibold' : 'text-ink-800')}>
                  {inv.dueDate ?? '—'}
                </span>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 py-4">
              <PartyBox
                role="Sprzedawca" name={inv.sellerName} nip={inv.sellerNip}
                ours={inv.direction === 'SALES'}
                clientId={inv.direction === 'PURCHASE' ? inv.counterpartyClientId : null}
                ourName={inv.ourCompanyName}/>
              <PartyBox
                role="Nabywca" name={inv.buyerName} nip={inv.buyerNip}
                ours={inv.direction === 'PURCHASE'}
                clientId={inv.direction === 'SALES' ? inv.counterpartyClientId : null}
                ourName={inv.ourCompanyName}/>
            </div>

            <div className="border-t border-ink-100 pt-4 flex flex-wrap gap-6 justify-between">
              {/* Sekcja „płatność" jak na dokumencie — panel obok jest wersją interaktywną. */}
              <div className="text-[12px] min-w-[190px]">
                <div className="text-[10px] uppercase tracking-wider text-ink-400 mb-1.5">Płatność</div>
                <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                  <span className="text-ink-400">Termin</span>
                  <span className={'font-mono ' + (overdue ? 'text-red-600 font-semibold' : 'text-ink-800')}>
                    {inv.dueDate ?? '—'}
                  </span>
                  <span className="text-ink-400">Zapłacono</span>
                  <span className="font-mono text-ink-800">{formatCurrency(inv.paidAmount, cur)}</span>
                  <span className="text-ink-400">Pozostało</span>
                  <span className={'font-mono ' + (inv.remainingAmount > 0 && !settled ? 'text-ink-900 font-semibold' : 'text-green-600')}>
                    {settled && inv.remainingAmount <= 0 ? 'rozliczona' : formatCurrency(inv.remainingAmount, cur)}
                  </span>
                </div>
              </div>
              <div className="flex-1 min-w-[280px] max-w-[380px] ml-auto text-[13px]">
                <AmountRow label="Wartość netto" value={formatCurrency(inv.totalNet, cur)}/>
                <AmountRow label="VAT" value={formatCurrency(inv.totalVat, cur)}/>
                <div className="flex items-baseline justify-between border-t border-ink-200 mt-1.5 pt-1.5">
                  <span className="text-[12px] font-semibold text-ink-700 uppercase tracking-wide">Razem brutto</span>
                  <span className="font-mono text-[18px] font-semibold text-ink-900">{formatCurrency(inv.totalGross, cur)}</span>
                </div>
                <NetVatBar net={inv.totalNet} vat={inv.totalVat} gross={inv.totalGross} currency={cur}/>
              </div>
            </div>

            {inv.notes && (
              <div className="mt-4 pt-3 border-t border-ink-100 text-[12px] text-ink-600 whitespace-pre-wrap">
                <span className="text-ink-400">Uwagi: </span>{inv.notes}
              </div>
            )}
          </div>
        </div>

        {/* ── Panel płatności ── */}
        <div className="grid gap-4">
          <div className="card">
            <div className="card-head">
              <span className="card-title">Rozliczenie</span>
              <span className={'pill ml-auto pill-' + inv.paymentStatusColor}>{inv.paymentStatusName}</span>
            </div>
            <div className="card-body">
              <div className="text-[10px] uppercase tracking-wide text-ink-400">
                {inv.remainingAmount > 0 && !settled ? 'Pozostało do zapłaty' : 'Zapłacono'}
              </div>
              <div className={'font-mono text-[24px] font-semibold leading-tight mb-3 '
                + (inv.remainingAmount > 0 && !settled ? (overdue ? 'text-red-600' : 'text-ink-900') : 'text-green-600')}>
                {formatCurrency(inv.remainingAmount > 0 && !settled ? inv.remainingAmount : inv.paidAmount, cur)}
              </div>

              <div className="h-2 rounded-full bg-ink-100 overflow-hidden mb-1">
                <div className={'h-full rounded-full transition-all ' + (paidPct >= 100 ? 'bg-green-500' : 'bg-water-500')}
                     style={{ width: paidPct + '%' }}/>
              </div>
              <div className="flex justify-between text-[11px] text-ink-400 mb-3">
                <span>zapłacono {formatCurrency(inv.paidAmount, cur)}</span>
                <span>{paidPct}% z {formatCurrency(inv.totalGross, cur)}</span>
              </div>

              <DeadlineBar issueDate={inv.issueDate} dueDate={inv.dueDate} paidDate={inv.paidDate}
                           settled={settled} dueIn={dueIn}/>

              <div className="grid grid-cols-2 gap-y-1.5 text-[12px] mt-3 pt-3 border-t border-ink-100">
                <KV k="Data zapłaty" v={inv.paidDate}/>
                <KV k="Waluta" v={cur}/>
              </div>
              {inv.paymentNote && (
                <div className="text-[12px] text-ink-600 mt-2 whitespace-pre-wrap">
                  <span className="text-ink-400">Uwagi do płatności: </span>{inv.paymentNote}
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-head"><span className="card-title">Dane dokumentu</span></div>
            <div className="card-body grid grid-cols-2 gap-y-1.5 text-[12px]">
              <KV k="Źródło" v={inv.source === 'KSEF' ? 'KSeF' : 'Wprowadzona ręcznie'}/>
              <KV k="Nasza firma" v={inv.ourCompanyName}/>
              <KV k="Kontrahent w bazie" v={inv.counterpartyClientName}
                  to={inv.counterpartyClientId ? `/klienci/${inv.counterpartyClientId}` : undefined}/>
              <KV k="Nr w systemie" v={'#' + inv.id}/>
            </div>
          </div>
        </div>
      </div>

      {/* ── Prace rozliczone tą fakturą ──
          Odpowiedź na „za co to było" — bez tego faktura zbiorcza na trzy studnie jest
          nierozszyfrowywalna po pół roku. Widoczne tylko przy uprawnieniu do rejestru prac. */}
      {canSeeWorks && (
        <div className="card mt-4">
          <div className="card-head">
            <span className="card-title">Prace rozliczone tą fakturą</span>
            <span className="text-[11px] text-ink-400">({works?.length ?? 0})</span>
            <Link to="/prace" className="ml-auto btn btn-ghost btn-sm">Rejestr prac →</Link>
          </div>
          <div className="card-body !p-0 overflow-x-auto">
            {works == null ? (
              <div className="p-5 text-center text-[12px] text-ink-400">Ładowanie…</div>
            ) : works.length === 0 ? (
              <div className="p-5 text-center text-[12px] text-ink-400">
                Żadna praca nie wskazuje tej faktury. Powiązanie tworzy się na karcie pracy
                („Przypnij fakturę") — jedna faktura może objąć kilka prac.
              </div>
            ) : (
              <table className="table-hw">
                <thead>
                  <tr><th>Lp.</th><th>Klient</th><th>Opis prac</th><th>Rodzaj</th><th>Status</th><th>Wykonano</th></tr>
                </thead>
                <tbody>
                  {works.map(w => (
                    <tr key={w.id} className="hover:bg-ink-50 cursor-pointer" onClick={() => navigate(`/prace/${w.id}`)}>
                      <td className="font-mono text-[12px] text-ink-500">{w.seqNo}</td>
                      <td className="text-[13px] text-ink-800 truncate max-w-[200px]">{w.clientName ?? '—'}</td>
                      <td className="text-[13px] text-ink-800 truncate max-w-[320px]">{w.title}</td>
                      <td><span className="pill pill-earth">{w.kindName}</span></td>
                      <td><span className={'pill pill-' + w.statusColor}>{w.statusName}</span></td>
                      <td className="font-mono text-[12px] text-ink-600">{w.firstDate ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ── Przelewy z wyciągu ── */}
      <div className="card mt-4">
        <div className="card-head">
          <span className="card-title">Przelewy z wyciągu bankowego</span>
          <span className="text-[11px] text-ink-400">({data.payments.length})</span>
          <Link to="/bank" className="ml-auto btn btn-ghost btn-sm">Bank / rozliczenia →</Link>
        </div>
        <div className="card-body !p-0 overflow-x-auto">
          {data.payments.length === 0 ? (
            <div className="p-5 text-center text-[12px] text-ink-400">
              Brak przelewów powiązanych z tą fakturą. Dopasowania powstają przy imporcie wyciągu
              albo synchronizacji z bankiem.
            </div>
          ) : (
            <table className="table-hw">
              <thead>
                <tr>
                  <th>Data księgowania</th>
                  <th className="text-right">Kwota</th>
                  <th>Kontrahent</th>
                  <th>Tytuł</th>
                  <th>Dopasowanie</th>
                </tr>
              </thead>
              <tbody>
                {data.payments.map(t => (
                  <tr key={t.id} className="!cursor-default">
                    <td className="font-mono text-[12px] text-ink-600">{t.bookingDate}</td>
                    <td className={'text-right font-mono text-[13px] ' + (t.amount < 0 ? 'text-ink-900' : 'text-green-600')}>
                      {formatCurrency(t.amount, t.currency || cur)}
                    </td>
                    <td className="text-[13px] text-ink-800">
                      <div className="truncate max-w-[220px]" title={t.counterpartyName ?? undefined}>{t.counterpartyName ?? '—'}</div>
                    </td>
                    <td className="text-[12px] text-ink-500">
                      <div className="truncate max-w-[280px]" title={t.title ?? undefined}>{t.title ?? '—'}</div>
                    </td>
                    <td><span className={'pill pill-' + t.matchStatusColor}>{t.matchStatusName}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Skany faktury (V153) — dla dokumentów spoza KSeF to jedyne miejsce z plikiem ── */}
      <div className="mt-4">
        <ArchiveFilesCard scope="invoice" scopeId={inv.id} scopeName={inv.invoiceNumber ?? undefined}/>
      </div>

      {/* ── Historia dokumentu ── */}
      <div className="card mt-4">
        <div className="card-head">
          <span className="card-title">Historia dokumentu</span>
          <span className="text-[11px] text-ink-400">({data.history.length})</span>
        </div>
        <div className="card-body">
          {data.history.length === 0 ? (
            <div className="py-3 text-center text-[12px] text-ink-400">Brak zapisanych zdarzeń.</div>
          ) : (
            <ol className="border-l border-ink-100 ml-1.5">
              {data.history.map(h => <HistoryItem key={h.id} entry={h}/>)}
            </ol>
          )}
        </div>
      </div>

      {payModal && meta && (
        <PaymentModal invoice={inv} statuses={meta.paymentStatuses}
                      onClose={() => setPayModal(false)}
                      onSaved={() => { setPayModal(false); reload(); }}/>
      )}
    </div>
  );
}

// ── Elementy podglądu ───────────────────────────────────────────────────────

function KV({ k, v, to }: { k: string; v?: string | number | null; to?: string }) {
  return (
    <>
      <span className="text-ink-400">{k}</span>
      <span className="text-ink-800 text-right">
        {v == null || v === '' ? '—' : to ? <Link to={to} className="text-water-700 hover:underline">{v}</Link> : v}
      </span>
    </>
  );
}

/** Strona faktury (sprzedawca / nabywca). „Twoja firma" oznacza naszą stronę dokumentu. */
function PartyBox({ role, name, nip, ours, clientId, ourName }: {
  role: string; name?: string | null; nip?: string | null;
  ours: boolean; clientId?: number | null; ourName?: string | null;
}) {
  return (
    <div className={'rounded-md border p-3 ' + (ours ? 'border-water-500/40 bg-water-50/50' : 'border-ink-100 bg-ink-50/50')}>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[10px] uppercase tracking-wider font-semibold text-ink-400">{role}</span>
        {ours && <span className="pill pill-water text-[10px]">Twoja firma{ourName ? ` · ${ourName}` : ''}</span>}
      </div>
      <div className="text-[13px] font-medium text-ink-900 leading-snug">
        {clientId
          ? <Link to={`/klienci/${clientId}`} className="text-water-700 hover:underline">{name ?? '—'}</Link>
          : (name ?? '—')}
      </div>
      <div className="text-[12px] text-ink-500 font-mono mt-0.5">{nip ? 'NIP ' + nip : 'NIP —'}</div>
    </div>
  );
}

function AmountRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between py-0.5">
      <span className="text-ink-500">{label}</span>
      <span className="font-mono text-ink-800">{value}</span>
    </div>
  );
}

/** Udział netto / VAT w kwocie brutto — szybka wizualna kontrola stawki podatku. */
function NetVatBar({ net, vat, gross, currency }: {
  net?: number | null; vat?: number | null; gross: number; currency: string;
}) {
  if (net == null || vat == null || gross <= 0) return null;
  const netPct = Math.min(100, Math.max(0, (net / gross) * 100));
  const vatPct = Math.min(100 - netPct, Math.max(0, (vat / gross) * 100));
  const rate = net > 0 ? Math.round((vat / net) * 100) : null;
  return (
    <div className="mt-4">
      <div className="flex h-2.5 rounded-full overflow-hidden bg-ink-100">
        <div className="bg-ink-500" style={{ width: netPct + '%' }} title={`Netto ${formatCurrency(net, currency)}`}/>
        <div className="bg-amber-500" style={{ width: vatPct + '%' }} title={`VAT ${formatCurrency(vat, currency)}`}/>
      </div>
      <div className="flex justify-between text-[11px] text-ink-400 mt-1">
        <span><span className="inline-block w-2 h-2 rounded-sm bg-ink-500 mr-1 align-middle"/>netto {Math.round(netPct)}%</span>
        <span>
          <span className="inline-block w-2 h-2 rounded-sm bg-amber-500 mr-1 align-middle"/>
          VAT {Math.round(vatPct)}%{rate != null ? ` (efektywnie ${rate}%)` : ''}
        </span>
      </div>
    </div>
  );
}

/**
 * Pasek terminu płatności: postęp od daty wystawienia do terminu, ze znacznikiem „dziś".
 * Po terminie pasek robi się czerwony i pokazuje liczbę dni zwłoki; po zapłacie — zielony.
 */
function DeadlineBar({ issueDate, dueDate, paidDate, settled, dueIn }: {
  issueDate?: string | null; dueDate?: string | null; paidDate?: string | null;
  settled: boolean; dueIn: number | null;
}) {
  if (!dueDate) return null;
  const issue = issueDate ? new Date(issueDate + 'T00:00:00').getTime() : null;
  const due = new Date(dueDate + 'T00:00:00').getTime();
  const today = new Date().setHours(0, 0, 0, 0);
  // Bez daty wystawienia zakładamy typowe 14 dni okna płatności — pasek ma tylko ilustrować napięcie terminu.
  const start = issue != null && issue < due ? issue : due - 14 * 86_400_000;
  const pct = Math.min(100, Math.max(0, ((today - start) / (due - start)) * 100));
  const overdue = !settled && dueIn != null && dueIn < 0;

  const label = settled
    ? (paidDate ? `zapłacono ${paidDate}` : 'rozliczona')
    : dueIn == null ? `termin ${dueDate}`
      : dueIn < 0 ? `po terminie o ${dni(-dueIn)}`
        : dueIn === 0 ? 'termin dziś' : `zostało ${dni(dueIn)}`;

  return (
    <div>
      <div className="flex justify-between text-[11px] text-ink-400 mb-1">
        <span>{issueDate ?? '—'}</span>
        <span className={overdue ? 'text-red-600 font-semibold' : settled ? 'text-green-600' : 'text-ink-500'}>{label}</span>
        <span>{dueDate}</span>
      </div>
      <div className="relative h-1.5 rounded-full bg-ink-100 overflow-hidden">
        <div className={'h-full rounded-full ' + (settled ? 'bg-green-500' : overdue ? 'bg-red-500' : 'bg-amber-500')}
             style={{ width: (settled || overdue ? 100 : pct) + '%' }}/>
      </div>
    </div>
  );
}

function HistoryItem({ entry }: { entry: InvoiceHistoryEntry }) {
  const who = entry.actorName || entry.actorLogin;
  return (
    <li className="relative pl-4 pb-3 last:pb-0">
      <span className="absolute -left-[4.5px] top-1.5 w-2 h-2 rounded-full bg-water-500"/>
      <div className="text-[12px] text-ink-800">
        <span className="font-semibold">{EVENT_LABELS[entry.eventType] ?? entry.eventType}</span>
        {entry.description && <span className="text-ink-600"> — {entry.description}</span>}
      </div>
      <div className="text-[11px] text-ink-400 mt-0.5">
        <span className="font-mono">{fmtDateTime(entry.at)}</span>
        {who && <span> · {who}</span>}
        {entry.impersonatorLogin && (
          <span className="pill text-[10px] bg-amber-100 text-amber-800 ml-1.5"
                title={`Akcję wykonał ${entry.impersonatorLogin}, pracując jako ${entry.actorLogin}`}>
            przez {entry.impersonatorLogin}
          </span>
        )}
      </div>
    </li>
  );
}

/**
 * Zmiana statusu płatności (kwota częściowa, data, uwagi). Szybkie „opłacona/cofnij" jest
 * przyciskiem w nagłówku i na liście faktur — tu ustawia się przypadki nietypowe.
 */
export function PaymentModal({ invoice, statuses, onClose, onSaved }: {
  invoice: Invoice;
  statuses: { id: InvoicePaymentStatus; name: string }[];
  onClose: () => void; onSaved: () => void;
}) {
  const [status, setStatus] = useState<InvoicePaymentStatus>(invoice.paymentStatus);
  const [paidAmount, setPaidAmount] = useState<string>(invoice.paidAmount ? String(invoice.paidAmount) : '');
  const [paidDate, setPaidDate] = useState<string>(invoice.paidDate ?? '');
  const [note, setNote] = useState<string>(invoice.paymentNote ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    api.setPaymentStatus(invoice.id, {
      status,
      paidAmount: paidAmount === '' ? null : Number(paidAmount),
      paidDate: paidDate || null,
      note: note.trim() || null,
    }).then(onSaved).catch(e2 => { setErr(e2?.message ?? String(e2)); setBusy(false); });
  };

  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: { status, paidAmount, paidDate, note }, onClose, busy,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <form onClick={e => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-[560px] max-h-[92vh] overflow-auto">
        <div className="px-5 py-3 border-b border-ink-100">
          <h2 className="text-[15px] font-semibold text-ink-900">Faktura {invoice.invoiceNumber ?? ''}</h2>
          <div className="text-[12px] text-ink-500">
            {invoice.directionName} · {formatCurrency(invoice.totalGross, invoice.currency)} ·{' '}
            {(invoice.direction === 'PURCHASE' ? invoice.sellerName : invoice.buyerName) ?? ''}
          </div>
        </div>
        <div className="p-5 grid gap-3">
          <label className="field">
            <span className="field-label">Status płatności</span>
            <SearchableSelect value={status} onChange={v => setStatus(v as InvoicePaymentStatus)}>
              {statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </SearchableSelect>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="field">
              <span className="field-label">Kwota zapłacona</span>
              <input type="number" step="0.01" value={paidAmount} onChange={e => setPaidAmount(e.target.value)} className="input font-mono text-right" placeholder={String(invoice.totalGross)}/>
            </label>
            <label className="field">
              <span className="field-label">Data zapłaty</span>
              <input type="date" value={paidDate} onChange={e => setPaidDate(e.target.value)} className="input"/>
            </label>
          </div>
          <label className="field">
            <span className="field-label">Uwagi</span>
            <textarea value={note} onChange={e => setNote(e.target.value)} className="textarea" rows={2}/>
          </label>
          {invoice.ksefNumber && <div className="text-[11px] text-ink-400">KSeF: <span className="font-mono">{invoice.ksefNumber}</span></div>}
          {err && <div className="text-red-600 text-[12px] p-2 bg-red-50 border border-red-200 rounded">{err}</div>}
          <div className="flex justify-end gap-2 mt-1">
            <button type="button" onClick={requestClose} disabled={busy} className="btn btn-secondary">Anuluj</button>
            <button type="submit" disabled={busy} className="btn btn-primary"><Icon name="check" className="w-3.5 h-3.5"/> {busy ? 'Zapisywanie…' : 'Zapisz'}</button>
          </div>
        </div>
      </form>
    </div>
  );
}
